import { useCallback, useMemo, useRef } from "react";
import { CellKeyDownEvent, ColDef, RowClassParams, RowClickedEvent, RowDoubleClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import useUiuxStore from "src/store/useUiuxStore";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";

import { aggridHeaderClass, commonBorderString } from "src/components/common/common";
import { showWarning } from "src/utils/toast/toastUtil";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";

// 새로운 구조 import
import { SearchableDto, CodeType } from "src/types/base";
import { getUiConfig, getApiConfig } from "src/utils/categoryUtils";
import { useGridCellSelection } from "src/utils/grid/GridHandlerContent";

// ColumnConfig 인터페이스 정의
interface ColumnConfig {
  headerName: string;
  field: string;
  flex?: number;
  width?: number;
  cellRenderer?: string;
  rendererConfig?: {
    type: 'commonCode';
    listKey: string;
    codeField?: string;
    nameField?: string;
  };
}


interface SearchTableProps<T extends SearchableDto> {
	codeType: CodeType;
	formName?: string;
	data: T[];
	selected: T | undefined;
	setSelected: (item: T | undefined) => void;
	onConfirm?: (data: T) => void;
	onCloseConfirm?: (data: T) => void;
	processType_focus?: string;
}

export const SearchTable = <T extends SearchableDto>(props: SearchTableProps<T>) => {
	const { 
		codeType,
		formName,
		data,
		selected,
		setSelected,
		onConfirm = () => { alert('모달 코드 등록후 행동할 동작이 없습니다.'); },
		onCloseConfirm = () => { /* alert('모달 코드 등록후 행동할 동작이 없습니다.'); */ },
		processType_focus = 'normal',
	} = props;

	const { setCodeSearchModalVisible } = useUiuxStore();
	const unitList = useCommonCodeStore((state) => state.unitList);
	const itemAccountList = useCommonCodeStore((state) => state.itemAccountList);
	const gridRef = useRef<AgGridReact>(null);
	const setSearchColumnState = useCallback(() => {}, []);

	// 설정에서 컬럼 정의와 코드 필드 가져오기 (새로운 구조)
	const config = useMemo(() => {
		// 공통코드 스토어 값을 전달하여 동적 컬럼 생성
		const storeData = { 
			unitList,
			itemAccountList
		};
		const uiConfig = getUiConfig(codeType, storeData);
		const apiConfig = getApiConfig(codeType);
		
		if (!uiConfig || !apiConfig) {
			console.warn(`Missing config for codeType: ${codeType}`);
			return { columns: [], codeField: '', nameField: '' };
		}
		
		return {
			columns: uiConfig.columns,
			codeField: apiConfig.codeField,
			nameField: apiConfig.nameField,
			emptyMessage: uiConfig.emptyMessage
		};
	}, [codeType, unitList, itemAccountList]);

	// 범용 공통코드 렌더러 생성 함수
	const createCommonCodeRenderer = useCallback((
		list: any[], 
		fieldName: string, 
		codeField: string = 'minorCd', 
		nameField: string = 'codeNm'
	) => {
		return (params: any) => {
			if (!params.data) return '';
			const codeValue = params.data[fieldName];
			if (codeValue === null || codeValue === undefined) return '';
			
			const foundItem = list.find((item: any) => item[codeField] === codeValue);
			return foundItem ? foundItem[nameField] : codeValue;
		};
	}, []);

	// 특별한 셀 렌더러 생성 함수 (방안 3: rendererConfig 기반)
	const createCellRenderer = useCallback((rendererType: string, rendererConfig?: ColumnConfig['rendererConfig'], fieldName?: string) => {
		// 범용 공통코드 렌더러 처리
		if (rendererType === 'commonCodeRenderer' && rendererConfig && fieldName) {
			const { listKey, codeField = 'minorCd', nameField = 'codeNm' } = rendererConfig;
			const commonCodeLists: Record<string, any[]> = {
				unitList,
				itemAccountList,
			};
			const list = commonCodeLists[listKey] || [];
			
			if (list.length > 0) {
				return createCommonCodeRenderer(list, fieldName, codeField, nameField);
			}
		}
		
		// 기타 특수 렌더러는 여기에 추가
		return undefined;
	}, [unitList, itemAccountList, createCommonCodeRenderer]);

	// 컬럼 정의 생성
	const columnDefs: ColDef<any>[] = useMemo(() => {
		return config.columns.map((col: ColumnConfig) => {
			const colDef: ColDef<any> = {
				headerName: col.headerName,
				field: col.field,
				colId: col.field,
				cellStyle: { borderRight: commonBorderString },
				headerClass: aggridHeaderClass
			};

			// 순번 컬럼 특별 처리
			if (col.field === 'rowIndex') {
				colDef.valueGetter = 'node.rowIndex + 1';
				colDef.width = col.width;
			} else {
				// 코드검색 모달은 우클릭 "너비 맞추기" 기능 체감을 위해 flex 대신 width 기반으로 초기화
				// (flex 컬럼은 자동 너비 조정 후에도 레이아웃 재계산으로 변화가 잘 보이지 않을 수 있음)
				if (col.flex) {
					colDef.width = col.width ?? 140;
					colDef.minWidth = 50;
				} else if (col.width) {
					colDef.width = col.width;
				}
			}

			// 특별한 렌더러 설정 (방안 3: rendererConfig 기반)
			if (col.cellRenderer) {
				colDef.cellRenderer = createCellRenderer(col.cellRenderer, col.rendererConfig, col.field);
			}

			return colDef;
		});
	}, [config.columns, createCellRenderer]);

	/*
	 * [보류] 모달을 닫았다 다시 열 때 컬럼 너비를 복원해야 하면 아래 로직을 활성화한다.
	 *
	 * import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
	 * import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
	 * import { onGridReady } from 'src/utils/grid/gridStyleUtils';
	 * import { useState } from 'react';
	 * import { ColumnState } from 'ag-grid-community';
	 *
	 * const [searchColumnState, setSearchColumnState] = useState<ColumnState[] | null>(null);
	 * const columnStateKey = `CodeSearchModal-${codeType}-column-state`;
	 * const defaultColumnState = useMemo(
	 *     () => buildDefaultColumnStateFromDefs(columnDefs, []),
	 *     [columnDefs]
	 * );
	 * const restoredColumnDefs = useMemo(() => GridCustomColumnDefs({
	 *     baseColumnDefs: columnDefs,
	 *     columnState: searchColumnState,
	 *     headerNames: Object.fromEntries(
	 *         columnDefs.map((column) => [column.colId ?? '', column.headerName ?? ''])
	 *     ),
	 * }), [columnDefs, searchColumnState]);
	 * const handleGridReady = useCallback(() => {
	 *     onGridReady(gridRef, columnStateKey, setSearchColumnState, () => defaultColumnState);
	 * }, [columnStateKey, defaultColumnState]);
	 *
	 * 활성화할 때 useGridCellSelection에는 restoredColumnDefs를 전달하고,
	 * GridContextWrapper에는 defaultColumnState를, AgGridReact에는
	 * key={codeType}와 onGridReady={handleGridReady}를 추가한다.
	 */

	// 드래그 범위 선택 + Ctrl+C 복사 훅
	const {
		handleCellMouseDown,
		handleCellMouseOver,
		handleCellCopy: handleRangeCopyKeyDown,
		processedColDefs,
	} = useGridCellSelection({ columnDefs });

	// 그리드 데이터 생성 (필요한 필드만 추출)
	const gridData = useMemo(() => {
		return data.map((item) => {
			const gridItem: any = {};
			config.columns.forEach((col: ColumnConfig) => {
				if (col.field !== 'rowIndex') {
					gridItem[col.field] = (item as any)[col.field];
				}
			});
			return gridItem;
		});
	}, [data, config.columns]);

	const codeFields = useMemo(
		() => Array.isArray(config.codeField) ? config.codeField : [config.codeField],
		[config.codeField]
	);

	const hasCodeFieldValues = useCallback((row: unknown): boolean => {
		if (!row || codeFields.length === 0) return false;
		const rowData = row as Record<string, unknown>;
		return codeFields.every((field) => {
			const value = rowData[field];
			return value !== undefined && value !== null && value !== '';
		});
	}, [codeFields]);

	const isSameRow = useCallback((left: unknown, right: unknown): boolean => {
		if (!left || !hasCodeFieldValues(right)) return false;
		const leftData = left as Record<string, unknown>;
		const rightData = right as Record<string, unknown>;
		return codeFields.every((field) => leftData[field] === rightData[field]);
	}, [codeFields, hasCodeFieldValues]);

	const findItemByRowData = useCallback((rowData: unknown): T | undefined => {
		return data.find((item) => isSameRow(item, rowData));
	}, [data, isSameRow]);

	// 행 클릭 핸들러 (함수형 업데이트 사용)
	const handleRowClick = useCallback((e: RowClickedEvent) => {
		const foundItem = findItemByRowData(e.data);
		if (!foundItem) return;

		if (isSameRow(selected, foundItem)) {
			setSelected(undefined);
		} else {
			setSelected(foundItem);
		}
	}, [findItemByRowData, isSameRow, selected, setSelected]);

	/** 더블클릭·엔터 공통: 원본 data 행으로 확정 후 모달 닫기 */
	const confirmRow = useCallback(
		(rowData: unknown) => {
			const searchedItem = findItemByRowData(rowData);

			if (!searchedItem) {
				showWarning(`${codeFields.join(', ')}이(가) 존재하지 않습니다.`);
				return;
			}

			if (onConfirm) onConfirm(searchedItem);
			setCodeSearchModalVisible(false);

			if (onCloseConfirm) onCloseConfirm(searchedItem as T);
		},
		[findItemByRowData, codeFields, onConfirm, setCodeSearchModalVisible, onCloseConfirm]
	);

	const handleRowDoubleClick = useCallback(
		(e: RowDoubleClickedEvent) => {
			confirmRow(e.data);
		},
		[confirmRow]
	);

	/** Ctrl+C: 범위 복사 / Enter: 행 확정 */
	const handleCellKeyDown = useCallback(
		(e: CellKeyDownEvent) => {
			const ev = e.event as KeyboardEvent | undefined | null;
			if (!ev) return;

			// Ctrl+C → 드래그 선택 범위를 클립보드에 복사
			if ((ev.ctrlKey || ev.metaKey) && ev.key === 'c') {
				handleRangeCopyKeyDown(e);
				return;
			}

			// Enter → 행 확정 (기존 로직)
			if (ev.key !== 'Enter' || ev.ctrlKey || ev.altKey || ev.metaKey) return;
			const rowData = e.node?.data;
			if (!hasCodeFieldValues(rowData)) return;
			ev.preventDefault();
			ev.stopPropagation();
			confirmRow(rowData);
		},
		[handleRangeCopyKeyDown, confirmRow, hasCodeFieldValues]
	);

	// 행 클래스 규칙
	const rowClassRules: Record<string, (params: RowClassParams) => boolean> = useMemo(() => {
		return {
			'selected': (params: RowClassParams) => isSameRow(selected, params.data),
			'hover': (params: RowClassParams) => !isSameRow(selected, params.data),
		};
	}, [selected, isSameRow]);

	const defaultColDef = useMemo<ColDef<any>>(() => ({
		sortable: true,
		resizable: true,
		cellClass: 'border-r-2 border-gray-300',
		hide: false,
	}), []);

	return (
		<GridContextWrapper<any>
			gridRef={gridRef}
			paddedGridData={gridData}
			initSelected={() => setSelected(undefined)}
			defaultRowData={{}}
			baseColumnDefs={columnDefs}
			setColumnState={setSearchColumnState}
			localStorageKey={`CodeSearchModal-${codeType}-column-state`}
			contextMenuZIndex={10080}
		>
			<AgGridReact
				ref={gridRef}
				rowData={gridData}
				columnDefs={processedColDefs}
				overlayNoRowsTemplate={`<span style="padding: 32px;">${config.emptyMessage ?? '조회된 데이터가 없습니다.'}</span>`}
				suppressScrollOnNewData={true}
				suppressColumnVirtualisation={true}
				enableRangeSelection={true}
				defaultColDef={defaultColDef}
				onRowClicked={handleRowClick}
				onRowDoubleClicked={handleRowDoubleClick}
				onCellMouseDown={handleCellMouseDown}
				onCellMouseOver={handleCellMouseOver}
				onCellKeyDown={handleCellKeyDown}
				rowClassRules={rowClassRules}
			/>
		</GridContextWrapper>
	);
};
